// Contexto de retorno a la app para el circuito de pago (/pay).
//
// Cuando el alta viene de un flow OAuth, al confirmar el pago el backend mintea
// un authorization_code y devuelve `redirect_to` → el usuario vuelve a la app ya
// logueado (ver mintAuthzRedirect en services/subscriptions/checkout). Para eso
// /pay necesita los authz params del flow, que nacieron una pantalla antes
// (/signup, o /verify al abrir el mail).
//
// Ese traspaso va por sessionStorage y NO por la URL, a propósito:
//
//   El `pt` (pay token) es compartible por diseño — 7 días de TTL, pensado para
//   que un familiar pague. Si /pay aceptara los authz params del query string,
//   cualquiera con un `pt` ajeno podría armar la URL con un code_challenge
//   PROPIO y, al pagar, cobrar un code canjeable por tokens del titular. O sea:
//   ascendería "puedo pagar esta suscripción" a "entro como el titular". Con el
//   contexto en sessionStorage no hay URL que forjar: solo lo tiene la pestaña
//   que efectivamente pasó por el signup o por el link del mail.
//
// sessionStorage (no localStorage) porque el alcance que queremos es exactamente
// el suyo: esta pestaña, esta pasada. Muere con la pestaña, sin TTL que mantener.
// Consecuencia asumida: si el usuario abre /pay en una pestaña NUEVA, no hay
// contexto y no hay redirect — mismo fallback que cuando el mail se abre en otro
// dispositivo (pantalla "listo, volvé a la app").
//
// El contexto queda atado al `pt`: si el que se presenta en /pay no es el mismo
// del alta, se descarta. Sin eso, un contexto viejo en la pestaña podría mandar
// el pago de un usuario al flow OAuth de otro.

import type { VerifyAuthzParams } from '@/lib/api';

const KEY = 'authsys:pay-return';

interface StoredPayReturn {
  pt: string;
  authz: VerifyAuthzParams;
}

/**
 * Guarda el contexto de retorno antes de navegar a /pay. Llamado por /signup
 * (mismo dispositivo) y por /verify (link del mail, solo si la cookie `av`
 * confirmó que es el mismo navegador del alta).
 */
export function stashPayReturn(pt: string, authz: VerifyAuthzParams): void {
  try {
    const payload: StoredPayReturn = { pt, authz };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Storage bloqueado (modo restringido del browser): sin contexto, /pay cae a
    // la pantalla final sin redirect. Degradación, no error.
  }
}

/**
 * Lee el contexto de retorno para el `pt` que trae /pay en la URL. Devuelve
 * undefined si no hay contexto, si está corrupto o si es de OTRO pay token.
 */
export function readPayReturn(pt: string | null): VerifyAuthzParams | undefined {
  if (!pt) return undefined;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredPayReturn;
    if (parsed?.pt !== pt) return undefined;
    const a = parsed.authz;
    // El set mínimo para mintear el code. Si falta alguno, el backend lo
    // rechazaría igual — mejor no mandar nada y avisar por consola.
    if (!a?.client_id || !a.redirect_uri || !a.state || !a.code_challenge) return undefined;
    return a;
  } catch {
    return undefined;
  }
}

/** Descarta el contexto una vez usado (o al abandonar el circuito). */
export function clearPayReturn(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // idem stashPayReturn: si no hay storage, no hay nada que limpiar.
  }
}
